// @flow

import axios from 'axios';

import { message } from 'antd';

import { API_URL, BASE_URL } from './Constants';
import { getAuthToken, removeAuthToken } from './Helper';
import i18n from './i18n';

/**
 * CREATES A USEABLE API URL WITH PARAMS like: /api/test/:id/something/:visibility
 * @param  {string} endpoint      endpoint of API wihtout BASE_URL and params ^
 * @param  {object} replaceParams object with all params that need to be replaced ({id: 1, visibility: true})
 * @return {string}               Fianl API URL with params replaced by replaceParams obj
 */
const createApiUrl = (endpoint, replaceParams) => {
  let endpointWithParams = endpoint;
  if (replaceParams) {
    Object.entries(replaceParams).map(k => {
      endpointWithParams = endpointWithParams.replace(`:${k[0]}`, k[1]);
      return true;
    });
  }
  return API_URL + endpointWithParams;
};

/**
 * FORMAT OBJECT TO FORM DATA FOR API CALLS
 * @param  {object}   obj
 * @return {FromData}       JS FormData with appended key:values from obj
 */
const objToFormData = obj => {
  let formData = new FormData();
  Object.entries(obj).map(e => formData.append(e[0], e[1]));
  return formData;
};

/**
 * HANDLES 401 HTTP RESPONSE CASE
 * Removes token from local storage
 */
const handleTokenReject = () => {
  removeAuthToken();
  message.error(i18n.errors.error401);
  return true;
};

const ax = (addHeaders = {}) => {
  let axInst = axios.create({
    // withCredentials: true,
    timeout: 300000,
    headers: {
      Accept: 'application/json',
      ...(getAuthToken() !== null
        ? { Authorization: 'Bearer ' + getAuthToken() }
        : {}),
      ...addHeaders
      // Authorization: 'Basic ' + btoa('world-insight:preview')
    }
  });
  axInst.interceptors.response.use(null, function(error) {
    if (error.response) {
      if (error.response.status === 401) {
        handleTokenReject();
      }
    }
    return Promise.reject(error);
  });
  return axInst;
};

const endpoints = {
  meta: {
    navigation: '/meta/navigation',
    dictonaries: '/meta/dictionaries'
  },
  dashboard: {
    index: '/dashboard'
  },
  user_accounts: {
    login: '/user_accounts/login',
    logout: '/user_accounts/logout'
  },
  documentManager: {
    update: '/document_manager/:id',
    createAndUpdate: '/document_manager/create_and_update',
    general: '/document_manager?context=general',
    voyages: '/document_manager?context=voyages',
    voyageDates: '/document_manager?context=voyage_dates',
    voyageBookings: '/document_manager?context=bookings',
    voyagesDetail: '/document_manager/:id/edit',
    detachDocument: '/document_manager/:id/detach_document'
  },
  search: {
    voyages: '/search/voyages',
    voyageNames: '/search/voyages/voyage_names',
    voyageNamesWithoutVoyageCourse:
      '/search/voyages/voyage_names_without_voyage_course',
    voyagesPlain: '/search/voyages/voyages_plain',
    voyagesWithoutCourse: '/search/voyages/without_voyage_course',
    voyage_dates: '/search/voyage_dates',
    bookings: '/search/bookings'
  },
  remoteResources: {
    index: '/remote_resources'
  },
  voyageCourses: {
    index: '/voyage_courses',
    editAssignment: '/voyage_courses/:id/edit_assignment',
    archiveAssignment: '/voyage_courses/:id/archive_assignment',
    createCopy: '/voyage_courses/:id/create_copy',
    copyToVoyage: '/voyage_courses/:id/copy_to_voyage',
    edit: '/voyage_courses/:id/edit',
    get: '/voyage_courses/:id',
    delete: '/voyage_courses/:id',
    removeVoyageDate: '/voyage_courses/:id/remove_voyage_date',
    createVoyageDay: '/voyage_courses/:id/create_voyage_day',
    copy: '/voyage_courses/:id/copy',
    copyVoyageCourse: '/voyage_courses/:id/copy',
    createDivider: '/voyage_courses/:id/create_divider',
    createFromVoyage: '/voyage_courses/create_from_voyage',
    createFromVoyageDate: '/voyage_courses/create_from_voyage_date',
    deleteVoyageDay: '/voyage_courses/:id/destroy_voyage_day'
  },
  documentModules: {
    index: '/document_modules',
    edit: '/document_modules/:id/edit',
    update: '/document_modules/:id',
    assignments: {
      update: '/document_manager/:id/document_modules'
    },
    categories: {
      create: '/document_module_categories',
      update: '/document_module_categories/:id',
      edit: '/document_module_categories/:id/edit'
    }
  },
  companies: {
    agency_addresses: {
      index: '/companies/agency_addresses',
      publish: '/companies/agency_addresses/:id/publish',
      create_and_publish: '/companies/agency_addresses/create_and_publish',
      discard_draft: '/companies/agency_addresses/:id/discard_draft',
      archiv_index: '/companies/agency_addresses/discarded',
      archiv_edit:
        '/companies/agency_addresses/show_discarded?discarded_id=:id',
      archiv_bemerkungen_versions:
        '/companies/agency_addresses/bemerkungen_versions_discarded?discarded_id=:id',
      edit: '/companies/agency_addresses/:id/edit',
      update: '/companies/agency_addresses/:id',
      bemerkungen_versions:
        '/companies/agency_addresses/:id/bemerkungen_versions',
      destroy_contact_person:
        '/companies/agency_addresses/:id/destroy_contact_person',
      detach_image: '/companies/agency_addresses/:id/detach_image',
      detach_video: '/companies/agency_addresses/:id/detach_video',
      company_address_countries:
        '/companies/agency_addresses/company_address_countries'
    },
    hotel_addresses: {
      index: '/companies/hotel_addresses',
      publish: '/companies/hotel_addresses/:id/publish',
      create_and_publish: '/companies/hotel_addresses/create_and_publish',
      discard_draft: '/companies/hotel_addresses/:id/discard_draft',
      archiv_index: '/companies/hotel_addresses/discarded',
      archiv_edit: '/companies/hotel_addresses/show_discarded?discarded_id=:id',
      archiv_bemerkungen_versions:
        '/companies/hotel_addresses/bemerkungen_versions_discarded?discarded_id=:id',
      edit: '/companies/hotel_addresses/:id/edit',
      update: '/companies/hotel_addresses/:id',
      bemerkungen_versions:
        '/companies/hotel_addresses/:id/bemerkungen_versions',
      company_address_countries:
        '/companies/hotel_addresses/company_address_countries',
      detach_image: '/companies/hotel_addresses/:id/detach_image',
      detach_video: '/companies/hotel_addresses/:id/detach_video',
      voyage_variations: '/companies/hotel_addresses/voyage_variations'
    },
    tour_guide_addresses: {
      index: '/companies/tour_guide_addresses',
      create_and_publish: '/companies/tour_guide_addresses/create_and_publish',
      edit: '/companies/tour_guide_addresses/:id/edit',
      publish: '/companies/tour_guide_addresses/:id/publish',
      discard_draft: '/companies/tour_guide_addresses/:id/discard_draft',
      archiv_index: '/companies/tour_guide_addresses/discarded',
      archiv_edit:
        '/companies/tour_guide_addresses/show_discarded?discarded_id=:id',
      archiv_bemerkungen_versions:
        '/companies/tour_guide_addresses/bemerkungen_versions_discarded?discarded_id=:id',
      update: '/companies/tour_guide_addresses/:id',
      bemerkungen_versions:
        '/companies/tour_guide_addresses/:id/bemerkungen_versions',
      company_address_countries:
        '/companies/tour_guide_addresses/company_address_countries',
      detach_image: '/companies/tour_guide_addresses/:id/detach_image',
      detach_video: '/companies/tour_guide_addresses/:id/detach_video'
    },
    airlines: {
      index: '/companies/airline_addresses',
      create_and_publish: '/companies/airline_addresses/create_and_publish',
      edit: '/companies/airline_addresses/:id/edit',
      publish: '/companies/airline_addresses/:id/publish',
      discard_draft: '/companies/airline_addresses/:id/discard_draft',
      archiv_index: '/companies/airline_addresses/discarded',
      archiv_edit:
        '/companies/airline_addresses/show_discarded?discarded_id=:id',
      archiv_bemerkungen_versions:
        '/companies/airline_addresses/bemerkungen_versions_discarded?discarded_id=:id',
      destroy_contact_person:
        '/companies/airline_addresses/:id/destroy_contact_person',
      company_address_countries:
        '/companies/airline_addresses/company_address_countries',
      detach_image: '/companies/airline_addresses/:id/detach_image',
      update: '/companies/airline_addresses/:id',
      bemerkungen_versions:
        '/companies/airline_addresses/:id/bemerkungen_versions'
    }
  },
  country_infos: {
    // create
    index: '/country_infos',
    edit: '/country_infos/:id/edit',
    update: '/country_infos/:id',
    update_element: '/country_infos/:id/update_element',
    publish: '/country_infos/:id/publish',
    discard_draft: '/country_infos/:id/discard_draft',
    append_element: '/country_infos/:id/append',
    detach_element: '/country_infos/:id/detach',
    countries: '/country_infos/countries?without_info=true',
    hide_editable_element: '/country_infos/:id/hide_editable_element',
    request_pdf: '/country_infos/:id/request_pdf'
  },
  country_info_templates: {
    // create
    index: '/country_info_templates',
    edit: '/country_info_templates',
    publish: '/country_info_templates/:id/publish',
    discard_draft: '/country_info_templates/:id/discard_draft',
    update: '/country_info_templates/:id',
    update_element: '/country_info_templates/:id/update_element',
    append_element: '/country_info_templates/:id/append',
    detach_element: '/country_info_templates/:id/detach'
  },
  voyage_content: {
    index: '/voyage_contents',
    edit: '/voyage_contents/:id/edit',
    copy: '/voyage_contents/:id/copy',
    update: '/voyage_contents/:id',
    archive: '/voyage_contents/:id/archive',
    publish: '/voyage_contents/:id/publish',
    discard_draft: '/voyage_contents/:id/discard_draft',
    voyage_date_assignments: '/voyage_contents/:id/voyage_date_assignments',
    assign_voyage_date: '/voyage_contents/:id/assign_voyage_date',
    reject_voyage_date: '/voyage_contents/:id/reject_voyage_date',
    discard_image: '/voyage_contents/:id/discard_image',
    set_to_master: '/voyage_contents/:id/set_to_master',
    request_pdf: '/voyage_contents/:id/request_pdf'
  }
};

export default {
  target_url() {
    return {
      get: (url, params) => ax().get(url, { params } || {})
    };
  },

  dashboard() {
    return {
      index: () => ax().get(createApiUrl(endpoints.dashboard.index))
    };
  },

  user_accounts() {
    return {
      login: user => {
        return ax().post(createApiUrl(endpoints.user_accounts.login), {
          user_account: user
        });
      },
      logout: () => {
        return ax().delete(createApiUrl(endpoints.user_accounts.logout));
      }
    };
  },

  meta() {
    return {
      navigation: () => ax().get(createApiUrl(endpoints.meta.navigation)),
      dictonaries: () => ax().get(createApiUrl(endpoints.meta.dictonaries))
    };
  },

  document_manager() {
    return {
      /**
       * DOUCMENT MANGER INDEX (context=general)
       * @return {httpResponse} HTTP Response
       */
      general: () => ax().get(createApiUrl(endpoints.documentManager.general)),

      /**
       * VOYAGES INDEX
       * @return {httpResponse} HTTP Response
       */
      voyages: () => ax().get(createApiUrl(endpoints.documentManager.voyages)),

      /**
       * VOYAGES SHOW/EDIT
       * @param  {number} id      ID of Voyages Entry
       * @return {httpResponse}   HTTP Response
       */
      voyagesDetail: id =>
        ax().get(createApiUrl(endpoints.documentManager.voyagesDetail, { id })),

      /**
       * DOCUMENT MANAGER UPDATE ITEM
       * @param  {FormData} documentMangager  VoyageDetail Element
       * @return {httpResponse}               HTTP Response
       */
      update: (id, documentManager) => {
        const formData = objToFormData(documentManager);
        return ax().put(
          createApiUrl(endpoints.documentManager.update, { id }),
          formData
        );
      },

      createAndUpdate: documentManager => {
        const formData = objToFormData(documentManager);
        return ax().put(
          createApiUrl(endpoints.documentManager.createAndUpdate),
          formData
        );
      },

      /**
       * VOYAGE DATES - get all dates for all voyages or specfied by param
       * @param  {object} params  { voyage: :id } id of voyage
       * @return {httpResponse}   HTTP Response
       */
      voyagesDates: params => {
        return ax().get(createApiUrl(endpoints.documentManager.voyageDates), {
          params: {
            ...params
          }
        });
      },

      /**
       * VOYAGE BOOKINGS - get all bookings for all voyages or specfied by param
       * @param  {object} params  { voyage_date: :id } id of voyage_date
       * @return {httpResponse}   HTTP Response
       */
      voyagesBookings: params => {
        return ax().get(
          createApiUrl(endpoints.documentManager.voyageBookings),
          {
            params: {
              ...params
            }
          }
        );
      },

      /**
       * DOCUMENT MANAGER NEW
       * @param  {string} url     Target URL From Voyage Entry
       * @return {httpResponse}   HTTP Response
       */
      new: tUrl => ax().get(`${BASE_URL}${tUrl}`),

      attachDocument: (attach_document_url, fileId, documentTypeId) => {
        let formData = new FormData();
        formData.append('remote_resource_id', fileId);
        formData.append('document_type_id', documentTypeId);
        return ax().put(attach_document_url, formData);
      },

      /**
       * DETACH DOCUMENT FROM DOCUMENT_TYPE
       * @param  {number} id          document/file id
       * @param  {number} documentId  document_type id
       * @return {httpResponse}       HTTP Response
       */
      detachDocument: (id, documentId) => {
        const formData = new FormData();
        formData.append('document_id', documentId);
        return ax().post(
          createApiUrl(endpoints.documentManager.detachDocument, {
            id
          }),
          formData
        );
      },
      download: url => {
        ax()
          .get(url)
          .then(response => {
            const link = document.createElement('a');
            link.href = response.data.url;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          });
      }
    };
  },

  search() {
    return {
      voyages: params =>
        ax().get(createApiUrl(endpoints.search.voyages), { params }),

      voyageNames: params =>
        ax().get(createApiUrl(endpoints.search.voyageNames), { params }),

      voyageNamesWithoutVoyageCourse: params =>
        ax().get(
          createApiUrl(endpoints.search.voyageNamesWithoutVoyageCourse),
          { params }
        ),

      voyagePlain: params =>
        ax().get(createApiUrl(endpoints.search.voyagesPlain), { params }),

      voyagesWithoutCourse: params =>
        ax().get(createApiUrl(endpoints.search.voyagesWithoutCourse), {
          params
        }),

      voyageDates: params =>
        ax().get(createApiUrl(endpoints.search.voyage_dates), { params }),

      bookings: params =>
        ax().get(createApiUrl(endpoints.search.bookings), { params })
    };
  },

  remote_resources() {
    return {
      index: params =>
        ax().get(createApiUrl(endpoints.remoteResources.index), { params })
    };
  },

  voyage_courses() {
    return {
      index: params =>
        ax().get(createApiUrl(endpoints.voyageCourses.index), { params }),

      editAssignment: id =>
        ax().get(createApiUrl(endpoints.voyageCourses.editAssignment, { id })),

      archiveAssignment: (id, params) =>
        ax().post(
          createApiUrl(endpoints.voyageCourses.archiveAssignment, { id }),
          objToFormData(params)
        ),

      createCopy: (id, params) =>
        ax().post(
          createApiUrl(endpoints.voyageCourses.createCopy, { id }),
          objToFormData(params)
        ),

      copyToVoyage: (id, params) =>
        ax().post(
          createApiUrl(endpoints.voyageCourses.copyToVoyage, { id }),
          objToFormData(params)
        ),

      delete: id =>
        ax().delete(createApiUrl(endpoints.voyageCourses.delete, { id }), {}),

      createFromVoyage: params =>
        ax().post(
          createApiUrl(endpoints.voyageCourses.createFromVoyage, {}),
          params
        ),

      createFromVoyageDate: params =>
        ax().post(
          createApiUrl(endpoints.voyageCourses.createFromVoyageDate, {}),
          params
        ),
      removeVoyageDate: (id, params) =>
        ax().post(
          createApiUrl(endpoints.voyageCourses.removeVoyageDate, { id }),
          params
        ),
      update: (id, params) =>
        ax().put(
          createApiUrl(endpoints.voyageCourses.get, { id }),
          objToFormData(params)
        ),
      edit: id => ax().get(createApiUrl(endpoints.voyageCourses.edit, { id })),
      createVoyageDay: (id, params) =>
        ax().post(
          createApiUrl(endpoints.voyageCourses.createVoyageDay, { id }),
          objToFormData(params)
        ),
      createDivider: (id, params) =>
        ax().post(
          createApiUrl(endpoints.voyageCourses.createDivider, { id }),
          objToFormData(params)
        ),
      deleteVoyageDay: (itemId, courseId) => {
        return ax().delete(
          createApiUrl(endpoints.voyageCourses.deleteVoyageDay, {
            id: courseId
          }) +
            '?voyage_day_id=' +
            itemId
        );
      }
    };
  },

  document_modules() {
    return {
      index: params =>
        ax().get(createApiUrl(endpoints.documentModules.index), { params }),

      create: params =>
        ax().post(createApiUrl(endpoints.documentModules.index), params),

      edit: id =>
        ax().get(createApiUrl(endpoints.documentModules.edit, { id })),

      update: (id, params) =>
        ax().put(
          createApiUrl(endpoints.documentModules.update, { id }),
          params
        ),

      assignments() {
        return {
          update: (id, params) =>
            ax().put(
              createApiUrl(endpoints.documentModules.assignments.update, {
                id
              }),
              objToFormData(params)
            )
        };
      },

      categories() {
        return {
          index: params =>
            ax().get(
              createApiUrl(endpoints.documentModules.categories.create),
              { params }
            ),
          create: params =>
            ax().post(
              createApiUrl(endpoints.documentModules.categories.create),
              objToFormData(params)
            ),
          edit: id =>
            ax().get(
              createApiUrl(endpoints.documentModules.categories.edit, { id })
            ),
          update: (id, params) =>
            ax().put(
              createApiUrl(endpoints.documentModules.categories.update, { id }),
              objToFormData(params)
            )
        };
      }
    };
  },
  companies() {
    return {
      agency_addresses() {
        return {
          index: params =>
            ax().get(createApiUrl(endpoints.companies.agency_addresses.index), {
              params
            }),
          create: params =>
            ax().post(
              createApiUrl(endpoints.companies.agency_addresses.index),
              params
            ),
          createAndPublish: params =>
            ax().post(
              createApiUrl(
                endpoints.companies.agency_addresses.create_and_publish
              ),
              params
            ),
          edit: id =>
            ax().get(
              createApiUrl(endpoints.companies.agency_addresses.edit, { id })
            ),
          update: (id, params) =>
            ax().put(
              createApiUrl(endpoints.companies.agency_addresses.update, { id }),
              params
            ),
          publish: (id, params) =>
            ax().put(
              createApiUrl(endpoints.companies.agency_addresses.publish, {
                id
              }),
              params
            ),
          discardDraft: id =>
            ax().get(
              createApiUrl(endpoints.companies.agency_addresses.discard_draft, {
                id
              })
            ),
          delete: id =>
            ax().delete(
              createApiUrl(endpoints.companies.agency_addresses.update, { id })
            ),
          bemerkungenVersions: id =>
            ax().get(
              createApiUrl(
                endpoints.companies.agency_addresses.bemerkungen_versions,
                { id }
              )
            ),
          destroyContactPerson: (id, params) =>
            ax().post(
              createApiUrl(
                endpoints.companies.agency_addresses.destroy_contact_person,
                { id }
              ),
              params
            ),
          detachImage: (id, fileId) =>
            ax().post(
              createApiUrl(endpoints.companies.agency_addresses.detach_image, {
                id
              }),
              {
                company_address_image_id: fileId
              }
            ),
          detachVideo: (id, fileId) =>
            ax().post(
              createApiUrl(endpoints.companies.agency_addresses.detach_video, {
                id
              }),
              {
                company_address_video_id: fileId
              }
            ),
          companyAddressCountries: () =>
            ax().get(
              createApiUrl(
                endpoints.companies.agency_addresses.company_address_countries
              )
            )
        };
      },
      archiv_agency_addresses() {
        return {
          index: params =>
            ax().get(
              createApiUrl(endpoints.companies.agency_addresses.archiv_index),
              {
                params
              }
            ),
          edit: id =>
            ax().get(
              createApiUrl(endpoints.companies.agency_addresses.archiv_edit, {
                id
              })
            ),
          bemerkungenVersions: id =>
            ax().get(
              createApiUrl(
                endpoints.companies.agency_addresses
                  .archiv_bemerkungen_versions,
                { id }
              )
            )
        };
      },
      hotel_addresses() {
        return {
          index: params =>
            ax().get(createApiUrl(endpoints.companies.hotel_addresses.index), {
              params
            }),
          create: params => {
            return ax().post(
              createApiUrl(endpoints.companies.hotel_addresses.index),
              params
            );
          },
          createAndPublish: params =>
            ax().post(
              createApiUrl(
                endpoints.companies.hotel_addresses.create_and_publish
              ),
              params
            ),
          edit: id =>
            ax().get(
              createApiUrl(endpoints.companies.hotel_addresses.edit, { id })
            ),
          update: (id, params) =>
            ax().put(
              createApiUrl(endpoints.companies.hotel_addresses.update, { id }),
              params
            ),
          publish: (id, params) =>
            ax().put(
              createApiUrl(endpoints.companies.hotel_addresses.publish, {
                id
              }),
              params
            ),
          discardDraft: id =>
            ax().get(
              createApiUrl(endpoints.companies.hotel_addresses.discard_draft, {
                id
              })
            ),
          delete: id =>
            ax().delete(
              createApiUrl(endpoints.companies.hotel_addresses.update, { id })
            ),

          detachImage: (id, fileId) =>
            ax().post(
              createApiUrl(endpoints.companies.hotel_addresses.detach_image, {
                id
              }),
              {
                company_address_image_id: fileId
              }
            ),
          bemerkungenVersions: id =>
            ax().get(
              createApiUrl(
                endpoints.companies.hotel_addresses.bemerkungen_versions,
                { id }
              )
            ),

          detachVideo: (id, fileId) =>
            ax().post(
              createApiUrl(endpoints.companies.hotel_addresses.detach_video, {
                id
              }),
              {
                company_address_video_id: fileId
              }
            ),
          voyageVariations: () =>
            ax().get(
              createApiUrl(
                endpoints.companies.hotel_addresses.voyage_variations
              )
            ),
          companyAddressCountries: () =>
            ax().get(
              createApiUrl(
                endpoints.companies.hotel_addresses.company_address_countries
              )
            )
        };
      },
      archiv_hotel_addresses() {
        return {
          index: params =>
            ax().get(
              createApiUrl(endpoints.companies.hotel_addresses.archiv_index),
              {
                params
              }
            ),
          edit: id =>
            ax().get(
              createApiUrl(endpoints.companies.hotel_addresses.archiv_edit, {
                id
              })
            ),
          bemerkungenVersions: id =>
            ax().get(
              createApiUrl(
                endpoints.companies.hotel_addresses.archiv_bemerkungen_versions,
                { id }
              )
            )
        };
      },
      guide_addresses() {
        return {
          index: params =>
            ax().get(
              createApiUrl(endpoints.companies.tour_guide_addresses.index),
              {
                params
              }
            ),
          create: params =>
            ax().post(
              createApiUrl(endpoints.companies.tour_guide_addresses.index),
              params
            ),
          createAndPublish: params =>
            ax().post(
              createApiUrl(
                endpoints.companies.tour_guide_addresses.create_and_publish
              ),
              params
            ),
          edit: id =>
            ax().get(
              createApiUrl(endpoints.companies.tour_guide_addresses.edit, {
                id
              })
            ),
          update: (id, params) =>
            ax().put(
              createApiUrl(endpoints.companies.tour_guide_addresses.update, {
                id
              }),
              params
            ),
          publish: (id, params) =>
            ax().put(
              createApiUrl(endpoints.companies.tour_guide_addresses.publish, {
                id
              }),
              params
            ),
          discardDraft: id =>
            ax().get(
              createApiUrl(
                endpoints.companies.tour_guide_addresses.discard_draft,
                {
                  id
                }
              )
            ),

          delete: id =>
            ax().delete(
              createApiUrl(endpoints.companies.tour_guide_addresses.update, {
                id
              })
            ),
          detachImage: (id, fileId) =>
            ax().post(
              createApiUrl(
                endpoints.companies.tour_guide_addresses.detach_image,
                {
                  id
                }
              ),
              {
                company_address_image_id: fileId
              }
            ),
          detachVideo: (id, fileId) =>
            ax().post(
              createApiUrl(
                endpoints.companies.tour_guide_addresses.detach_video,
                {
                  id
                }
              ),
              {
                company_address_video_id: fileId
              }
            ),
          bemerkungenVersions: id =>
            ax().get(
              createApiUrl(
                endpoints.companies.tour_guide_addresses.bemerkungen_versions,
                { id }
              )
            ),
          companyAddressCountries: () =>
            ax().get(
              createApiUrl(
                endpoints.companies.tour_guide_addresses
                  .company_address_countries
              )
            )
        };
      },
      archiv_guide_addresses() {
        return {
          index: params =>
            ax().get(
              createApiUrl(
                endpoints.companies.tour_guide_addresses.archiv_index
              ),
              {
                params
              }
            ),
          edit: id =>
            ax().get(
              createApiUrl(
                endpoints.companies.tour_guide_addresses.archiv_edit,
                {
                  id
                }
              )
            ),
          bemerkungenVersions: id =>
            ax().get(
              createApiUrl(
                endpoints.companies.tour_guide_addresses
                  .archiv_bemerkungen_versions,
                { id }
              )
            )
        };
      },
      airline_addresses() {
        return {
          index: params =>
            ax().get(createApiUrl(endpoints.companies.airlines.index), {
              params
            }),
          create: params =>
            ax().post(createApiUrl(endpoints.companies.airlines.index), params),
          createAndPublish: params =>
            ax().post(
              createApiUrl(endpoints.companies.airlines.create_and_publish),
              params
            ),
          edit: id =>
            ax().get(
              createApiUrl(endpoints.companies.airlines.edit, {
                id
              })
            ),
          update: (id, params) =>
            ax().put(
              createApiUrl(endpoints.companies.airlines.update, {
                id
              }),
              params
            ),
          publish: (id, params) =>
            ax().put(
              createApiUrl(endpoints.companies.airlines.publish, {
                id
              }),
              params
            ),
          discardDraft: id =>
            ax().get(
              createApiUrl(endpoints.companies.airlines.discard_draft, {
                id
              })
            ),
          delete: id =>
            ax().delete(
              createApiUrl(endpoints.companies.airlines.update, {
                id
              })
            ),
          bemerkungenVersions: id =>
            ax().get(
              createApiUrl(endpoints.companies.airlines.bemerkungen_versions, {
                id
              })
            ),
          detachImage: (id, fileId) =>
            ax().post(
              createApiUrl(endpoints.companies.airlines.detach_image, {
                id
              }),
              {
                company_address_image_id: fileId
              }
            ),
          destroyContactPerson: (id, params) =>
            ax().post(
              createApiUrl(
                endpoints.companies.airlines.destroy_contact_person,
                { id }
              ),
              params
            ),
          companyAddressCountries: () =>
            ax().get(
              createApiUrl(
                endpoints.companies.airlines.company_address_countries
              )
            )
        };
      },
      archiv_airline_addresses() {
        return {
          index: params =>
            ax().get(createApiUrl(endpoints.companies.airlines.archiv_index), {
              params
            }),
          edit: id =>
            ax().get(
              createApiUrl(endpoints.companies.airlines.archiv_edit, {
                id
              })
            ),
          bemerkungenVersions: id =>
            ax().get(
              createApiUrl(
                endpoints.companies.airlines.archiv_bemerkungen_versions,
                {
                  id
                }
              )
            )
        };
      }
    };
  },

  country_infos() {
    return {
      index: params =>
        ax().get(createApiUrl(endpoints.country_infos.index), {
          params
        }),
      countries: params =>
        ax().get(createApiUrl(endpoints.country_infos.countries), {
          params
        }),
      create: params =>
        ax().post(createApiUrl(endpoints.country_infos.index), params),
      edit: (id, params) =>
        ax().get(
          createApiUrl(endpoints.country_infos.edit, {
            id
          }),
          {
            params
          }
        ),
      discardDraft: (id, params) =>
        ax().post(
          createApiUrl(endpoints.country_infos.discard_draft, {
            id
          })
        ),
      updateElement: (id, body) =>
        ax().put(
          createApiUrl(endpoints.country_infos.update_element, {
            id
          }),
          body
        ),
      publish: (id, body) =>
        ax().post(
          createApiUrl(endpoints.country_infos.publish, {
            id
          }),
          body
        ),
      appendElement: (id, body) =>
        ax().post(
          createApiUrl(endpoints.country_infos.append_element, { id }),
          body
        ),
      detachElement: (id, body) =>
        ax().post(
          createApiUrl(endpoints.country_infos.detach_element, { id }),
          body
        ),
      update: (id, params) =>
        ax().put(
          createApiUrl(endpoints.country_infos.update, {
            id
          }),
          params
        ),
      hideEditableElement: (id, body) =>
        ax().post(
          createApiUrl(endpoints.country_infos.hide_editable_element, { id }),
          body
        ),
      requestPdf: id =>
        ax().get(createApiUrl(endpoints.country_infos.request_pdf, { id }), {
          timeout: 60000
        })
    };
  },

  country_info_templates() {
    return {
      index: params =>
        ax().get(createApiUrl(endpoints.country_info_templates.index), {
          params
        }),
      create: params =>
        ax().post(createApiUrl(endpoints.country_info_templates.index), params),
      edit: (id, params) =>
        ax().get(
          createApiUrl(endpoints.country_info_templates.edit, {
            id
          }),
          {
            params
          }
        ),
      updateElement: (id, body) =>
        ax().put(
          createApiUrl(endpoints.country_info_templates.update, {
            id
          }),
          body
        ),
      discardDraft: (id, params) =>
        ax().post(
          createApiUrl(endpoints.country_info_templates.discard_draft, {
            id
          })
        ),
      publish: (id, body) =>
        ax().post(
          createApiUrl(endpoints.country_info_templates.publish, {
            id
          }),
          body
        ),
      appendElement: (id, body) =>
        ax().post(
          createApiUrl(endpoints.country_info_templates.append_element, {
            id
          }),
          body
        ),
      detachElement: (id, body) =>
        ax().post(
          createApiUrl(endpoints.country_info_templates.detach_element, {
            id
          }),
          body
        ),
      update: (id, params) =>
        ax().put(
          createApiUrl(endpoints.country_info_templates.update, {
            id
          }),
          params
        )
    };
  },

  voyage_content() {
    return {
      index: params =>
        ax().get(createApiUrl(endpoints.voyage_content.index), {
          params
        }),
      edit: (id, params) =>
        ax().get(
          createApiUrl(endpoints.voyage_content.edit, {
            id
          }),
          { params }
        ),
      voyageDateAssignments: id =>
        ax().get(
          createApiUrl(endpoints.voyage_content.voyage_date_assignments, {
            id
          })
        ),
      update: (id, body) =>
        ax().put(
          createApiUrl(endpoints.voyage_content.update, {
            id
          }),
          body
        ),
      publish: (id, body) =>
        ax().post(
          createApiUrl(endpoints.voyage_content.publish, {
            id
          }),
          body
        ),
      archive: (id, body) =>
        ax().post(
          createApiUrl(endpoints.voyage_content.archive, {
            id
          }),
          body
        ),
      discard: id =>
        ax().post(
          createApiUrl(endpoints.voyage_content.discard_draft, {
            id
          })
        ),
      setToMaster: (id, body) =>
        ax().post(
          createApiUrl(endpoints.voyage_content.set_to_master, {
            id
          })
        ),
      discard_image: (id, body) =>
        ax().post(
          createApiUrl(endpoints.voyage_content.discard_image, {
            id
          }),
          body
        ),
      assignVoyageDate: (id, body) =>
        ax().post(
          createApiUrl(endpoints.voyage_content.assign_voyage_date, {
            id
          }),
          body
        ),
      rejectVoyageDate: (id, body) =>
        ax().post(
          createApiUrl(endpoints.voyage_content.reject_voyage_date, { id }),
          body
        ),
      copy: (id, body) =>
        ax().post(
          createApiUrl(endpoints.voyage_content.copy, {
            id
          }),
          body
        ),
      requestPdf: id =>
        ax().get(
          createApiUrl(endpoints.voyage_content.request_pdf, {
            id
          }),
          { timeout: 60000 }
        )
    };
  }
};



// WEBPACK FOOTER //
// ./src/utils/ApiService.js